import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import { SearchAndReplace } from "@sereneinserenade/tiptap-search-and-replace";
import { toast } from "sonner";
import {
  Bookmark,
  Highlighter,
  Eraser,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  Replace,
  ReplaceAll,
  Copy,
} from "lucide-react";
import { NOTEPAD } from "@/constants/testIds";

const STABILO_COLORS = [
  { name: "yellow", label: "Kuning", value: "rgba(250, 204, 21, 0.45)", ring: "#facc15" },
  { name: "green", label: "Hijau", value: "rgba(74, 222, 128, 0.45)", ring: "#4ade80" },
  { name: "pink", label: "Pink", value: "rgba(244, 114, 182, 0.45)", ring: "#f472b6" },
  { name: "blue", label: "Biru", value: "rgba(96, 165, 250, 0.45)", ring: "#60a5fa" },
  { name: "orange", label: "Oranye", value: "rgba(251, 146, 60, 0.45)", ring: "#fb923c" },
];

export default function Editor({ note, onChange, onCursorChange, onBookmarksChange, saveStatus }) {
  const [lineCount, setLineCount] = useState(1);
  const [hoveredLine, setHoveredLine] = useState(null);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [findTerm, setFindTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [matchInfo, setMatchInfo] = useState({ total: 0, index: 0 });

  const bookmarks = useMemo(() => new Set(note.bookmarks || []), [note.bookmarks]);
  const editorRef = useRef(null);
  const canvasRef = useRef(null);
  const gutterRef = useRef(null);
  const findInputRef = useRef(null);
  const restoredRef = useRef(false);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        Highlight.configure({ multicolor: true }),
        Placeholder.configure({
          placeholder: "Mulai menulis di sini... gunakan sorot warna seperti stabilo untuk menandai bagian penting.",
        }),
        SearchAndReplace.configure({
          searchResultClass: "notepad-search-result",
          disableRegex: true,
        }),
      ],
      content: note.content || "",
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
        updateLineCount();
        refreshMatchInfo(editor);
      },
      onSelectionUpdate: ({ editor }) => {
        const pos = editor.state.selection.from;
        onCursorChange(pos);
      },
      editorProps: {
        attributes: {
          class:
            "prose prose-invert max-w-none focus:outline-none min-h-full px-6 py-4 text-[15px] leading-[1.9] font-mono text-[#E6E8EF]",
          "data-testid": NOTEPAD.editorCanvas,
        },
      },
    },
    [note.id]
  );

  const refreshMatchInfo = (ed) => {
    if (!ed) return;
    const s = ed.storage?.searchAndReplace;
    if (s) {
      setMatchInfo({
        total: s.results?.length || 0,
        index: (s.resultIndex ?? 0) + (s.results?.length ? 1 : 0),
      });
    }
  };

  const updateLineCount = useCallback(() => {
    if (!editorRef.current) return;
    const dom = editorRef.current.querySelector(".ProseMirror");
    if (!dom) return;
    const count = Math.max(1, dom.children.length);
    setLineCount(count);
  }, []);

  // Load content when note changes
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== (note.content || "")) {
      editor.commands.setContent(note.content || "", false);
    }
    restoredRef.current = false;
    setTimeout(updateLineCount, 50);
  }, [note.id, editor]);

  // Restore cursor position
  useEffect(() => {
    if (!editor || restoredRef.current) return;
    const pos = note.cursor_position || 0;
    if (pos > 0) {
      try {
        const size = editor.state.doc.content.size;
        const safe = Math.min(pos, size);
        editor.commands.focus();
        editor.commands.setTextSelection(safe);
      } catch {
        /* ignore */
      }
    }
    restoredRef.current = true;
  }, [editor, note.id, note.cursor_position]);

  // Update search when find term changes
  useEffect(() => {
    if (!editor) return;
    try {
      if (showFindReplace) {
        editor.commands.setSearchTerm(findTerm);
        editor.commands.setReplaceTerm(replaceTerm);
        refreshMatchInfo(editor);
      } else {
        editor.commands.setSearchTerm("");
        editor.commands.setReplaceTerm("");
      }
    } catch {
      /* ignore search compile errors – keep app alive */
    }
  }, [findTerm, replaceTerm, showFindReplace, editor]);

  // Keyboard shortcuts Ctrl+F / Ctrl+H
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setShowFindReplace(true);
        setShowReplace(false);
        setTimeout(() => findInputRef.current?.focus(), 50);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "h") {
        e.preventDefault();
        setShowFindReplace(true);
        setShowReplace(true);
        setTimeout(() => findInputRef.current?.focus(), 50);
      } else if (e.key === "Escape" && showFindReplace) {
        setShowFindReplace(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showFindReplace]);

  const handleScroll = () => {
    if (gutterRef.current && canvasRef.current) {
      gutterRef.current.scrollTop = canvasRef.current.scrollTop;
    }
  };

  const applyHighlight = (color) => {
    if (!editor) return;
    editor.chain().focus().toggleHighlight({ color: color.value }).run();
  };

  const clearHighlight = () => {
    if (!editor) return;
    editor.chain().focus().unsetHighlight().run();
  };

  const toggleBookmark = (lineNum) => {
    const next = new Set(bookmarks);
    if (next.has(lineNum)) next.delete(lineNum);
    else next.add(lineNum);
    onBookmarksChange(Array.from(next).sort((a, b) => a - b));
  };

  const scrollToBookmarks = () => {
    if (bookmarks.size === 0) return;
    const first = Math.min(...Array.from(bookmarks));
    const dom = editorRef.current?.querySelector(".ProseMirror");
    if (dom && dom.children[first - 1]) {
      dom.children[first - 1].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const copyLine = async (lineNum) => {
    const dom = editorRef.current?.querySelector(".ProseMirror");
    if (!dom || !editor) return;
    const el = dom.children[lineNum - 1];
    if (!el) return;
    const text = el.textContent || "";
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Baris ${lineNum} disalin`, { description: text.slice(0, 80) || "(baris kosong)" });

      // Move cursor to the END of the copied line so user can keep typing
      try {
        // Find the ProseMirror position for the end of the block at index (lineNum-1)
        let targetPos = 0;
        let idx = 0;
        editor.state.doc.forEach((node, offset) => {
          if (idx === lineNum - 1) {
            targetPos = offset + node.nodeSize - 1;
          }
          idx += 1;
        });
        if (targetPos > 0) {
          editor.chain().focus().setTextSelection(targetPos).run();
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      } catch {
        /* ignore cursor move failures */
      }
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const findNext = () => {
    if (!editor || !findTerm) return;
    editor.commands.nextSearchResult();
    refreshMatchInfo(editor);
    scrollToActiveMatch();
  };

  const findPrev = () => {
    if (!editor || !findTerm) return;
    editor.commands.previousSearchResult();
    refreshMatchInfo(editor);
    scrollToActiveMatch();
  };

  const replaceOne = () => {
    if (!editor || !findTerm) return;
    editor.commands.replace();
    refreshMatchInfo(editor);
  };

  const replaceAll = () => {
    if (!editor || !findTerm) return;
    const count = matchInfo.total;
    editor.commands.replaceAll();
    toast.success(`${count} penggantian selesai`);
    refreshMatchInfo(editor);
  };

  const scrollToActiveMatch = () => {
    setTimeout(() => {
      const el = editorRef.current?.querySelector(".notepad-search-result-current, .notepad-search-result");
      if (el && canvasRef.current) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 30);
  };

  return (
    <div
      data-testid={NOTEPAD.editorContainer}
      className="flex flex-col h-full bg-[#12141D] relative"
    >
      {/* Toolbar */}
      <div className="h-11 border-b border-[#2A2F45] flex items-center px-3 gap-2 bg-[#0A0B10]/60">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#12141D] border border-white/5">
          <Highlighter className="w-3.5 h-3.5 text-[#8B949E]" />
          <span className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono mr-1">Stabilo</span>
          {STABILO_COLORS.map((c) => (
            <button
              key={c.name}
              onClick={() => applyHighlight(c)}
              data-testid={NOTEPAD.stabiloBtn(c.name)}
              title={c.label}
              className="w-5 h-5 rounded-full border border-white/20 transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:ring-white/40"
              style={{ backgroundColor: c.ring }}
            />
          ))}
          <button
            onClick={clearHighlight}
            data-testid={NOTEPAD.stabiloClearBtn}
            title="Hapus sorotan"
            className="w-6 h-6 rounded-full flex items-center justify-center text-[#8B949E] hover:text-white hover:bg-white/10 transition-colors"
          >
            <Eraser className="w-3.5 h-3.5" />
          </button>
        </div>

        <button
          onClick={scrollToBookmarks}
          disabled={bookmarks.size === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-[#8B949E] hover:text-indigo-400 hover:bg-[#12141D] disabled:opacity-40 transition-colors font-mono"
          title="Lompat ke tanda pertama"
        >
          <Bookmark className="w-3.5 h-3.5" />
          {bookmarks.size} tanda
        </button>

        <button
          onClick={() => {
            setShowFindReplace(true);
            setShowReplace(true);
            setTimeout(() => findInputRef.current?.focus(), 50);
          }}
          data-testid={NOTEPAD.findReplaceToggle}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-[#8B949E] hover:text-indigo-400 hover:bg-[#12141D] transition-colors font-mono"
          title="Cari & Ganti (Ctrl+H)"
        >
          <Search className="w-3.5 h-3.5" />
          Cari &amp; Ganti
        </button>

        <div className="ml-auto flex items-center gap-2">
          <div
            data-testid={NOTEPAD.saveStatus}
            className={`flex items-center gap-1.5 text-xs font-mono ${
              saveStatus === "saving" ? "text-amber-400" : "text-emerald-400"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${
              saveStatus === "saving" ? "bg-amber-400 animate-pulse" : "bg-emerald-400"
            }`} />
            {saveStatus === "saving" ? "Menyimpan..." : "Tersimpan"}
          </div>
        </div>
      </div>

      {/* Find & Replace Panel */}
      {showFindReplace && (
        <div
          data-testid={NOTEPAD.findReplacePanel}
          className="border-b border-[#2A2F45] bg-[#0A0B10]/80 backdrop-blur px-3 py-2 flex flex-col gap-2"
        >
          <div className="flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
            <input
              ref={findInputRef}
              data-testid={NOTEPAD.findInput}
              value={findTerm}
              onChange={(e) => setFindTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (e.shiftKey) findPrev();
                  else findNext();
                }
              }}
              placeholder="Cari..."
              className="flex-1 bg-[#12141D] border border-[#2A2F45] focus:border-indigo-500 text-white text-sm px-2.5 py-1 rounded outline-none font-mono max-w-xs"
            />
            <span
              data-testid={NOTEPAD.findMatchCount}
              className="text-xs text-[#8B949E] font-mono min-w-[70px]"
            >
              {findTerm
                ? matchInfo.total > 0
                  ? `${matchInfo.index} / ${matchInfo.total}`
                  : "Tidak ada"
                : ""}
            </span>
            <button
              onClick={findPrev}
              data-testid={NOTEPAD.findPrevBtn}
              disabled={!findTerm || matchInfo.total === 0}
              title="Sebelumnya (Shift+Enter)"
              className="p-1.5 rounded text-[#8B949E] hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={findNext}
              data-testid={NOTEPAD.findNextBtn}
              disabled={!findTerm || matchInfo.total === 0}
              title="Selanjutnya (Enter)"
              className="p-1.5 rounded text-[#8B949E] hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowReplace(!showReplace)}
              className={`px-2 py-1 text-xs rounded font-mono transition-colors ${
                showReplace
                  ? "bg-indigo-500/20 text-indigo-300"
                  : "text-[#8B949E] hover:text-white hover:bg-white/10"
              }`}
              title="Buka panel ganti"
            >
              Ganti
            </button>
            <button
              onClick={() => setShowFindReplace(false)}
              data-testid={NOTEPAD.findReplaceClose}
              title="Tutup (Esc)"
              className="p-1.5 rounded text-[#8B949E] hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {showReplace && (
            <div className="flex items-center gap-2 pl-6">
              <Replace className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
              <input
                data-testid={NOTEPAD.replaceInput}
                value={replaceTerm}
                onChange={(e) => setReplaceTerm(e.target.value)}
                placeholder="Ganti dengan..."
                className="flex-1 bg-[#12141D] border border-[#2A2F45] focus:border-indigo-500 text-white text-sm px-2.5 py-1 rounded outline-none font-mono max-w-xs"
              />
              <button
                onClick={replaceOne}
                data-testid={NOTEPAD.replaceOneBtn}
                disabled={!findTerm || matchInfo.total === 0}
                className="px-3 py-1 text-xs rounded font-mono bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 border border-indigo-500/30 disabled:opacity-30 transition-colors flex items-center gap-1"
              >
                <Replace className="w-3 h-3" />
                Ganti
              </button>
              <button
                onClick={replaceAll}
                data-testid={NOTEPAD.replaceAllBtn}
                disabled={!findTerm || matchInfo.total === 0}
                className="px-3 py-1 text-xs rounded font-mono bg-indigo-500 text-white hover:bg-indigo-400 disabled:opacity-30 transition-colors flex items-center gap-1"
              >
                <ReplaceAll className="w-3 h-3" />
                Ganti Semua
              </button>
            </div>
          )}
        </div>
      )}

      {/* Editor + Gutter */}
      <div className="flex-1 flex overflow-hidden">
        {/* Gutter */}
        <div
          ref={gutterRef}
          data-testid={NOTEPAD.gutter}
          className="w-20 bg-[#0A0B10] border-r border-[#2A2F45] overflow-hidden select-none flex-shrink-0"
        >
          <div className="py-4">
            {Array.from({ length: lineCount }).map((_, i) => {
              const n = i + 1;
              const isBookmarked = bookmarks.has(n);
              const isHovered = hoveredLine === n;
              return (
                <div
                  key={n}
                  data-testid={NOTEPAD.gutterLine(n)}
                  onMouseEnter={() => setHoveredLine(n)}
                  onMouseLeave={() => setHoveredLine(null)}
                  className="flex items-center justify-end gap-1 pr-2 text-xs font-mono hover:bg-white/5 transition-colors group"
                  style={{ lineHeight: "1.9", fontSize: "15px" }}
                >
                  {/* Copy button on hover */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyLine(n);
                    }}
                    data-testid={NOTEPAD.copyLineBtn(n)}
                    title={`Salin baris ${n}`}
                    className={`p-0.5 rounded transition-all text-[#4B5563] hover:text-indigo-400 hover:bg-white/10 ${
                      isHovered ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <Copy className="w-3 h-3" />
                  </button>

                  {isBookmarked && (
                    <span
                      data-testid={NOTEPAD.bookmarkDot(n)}
                      onClick={() => toggleBookmark(n)}
                      className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_6px_rgba(99,102,241,0.8)] cursor-pointer"
                    />
                  )}
                  <button
                    onClick={() => toggleBookmark(n)}
                    title="Klik nomor untuk tandai baris"
                    className={`cursor-pointer bg-transparent border-0 p-0 font-mono ${
                      isBookmarked
                        ? "text-indigo-400 font-semibold"
                        : "text-[#4B5563] group-hover:text-[#8B949E]"
                    }`}
                    style={{ fontSize: "15px" }}
                  >
                    {n}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={canvasRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto"
        >
          <div ref={editorRef} className="min-h-full">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    </div>
  );
}
